import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Loader2, Plus, Edit2, Warehouse as WarehouseIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function RawMaterialsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rawItems, setRawItems] = useState([]);
  const [showRawModal, setShowRawModal] = useState(false);
  const [selectedRawItem, setSelectedRawItem] = useState(null);
  const [rawItemForm, setRawItemForm] = useState({
    name: '',
    is_active: true,
  });

  const [rawItemsPage, setRawItemsPage] = useState(1);
  const pageSize = 20;

  const rawItemsTotalPages = Math.max(1, Math.ceil(rawItems.length / pageSize));

  const pagedRawItems = useMemo(() => {
    const start = (rawItemsPage - 1) * pageSize;
    return rawItems.slice(start, start + pageSize);
  }, [rawItems, rawItemsPage]);

  const fetchRawItems = async () => {
    const response = await adminApi.getRawItems();
    setRawItems(response.data || []);
  };

  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const [rawItemsRes] = await Promise.all([
          adminApi.getRawItems(),
        ]);
        setRawItems(rawItemsRes.data || []);
      } catch (error) {
        toast.error('Failed to load raw materials');
      } finally {
        setLoading(false);
      }
    };

    fetchInitial();
  }, []);

  const resetForm = () => {
    setRawItemForm({ name: '', is_active: true });
    setSelectedRawItem(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowRawModal(true);
  };

  const handleOpenEdit = (item) => {
    setSelectedRawItem(item);
    setRawItemForm({
      name: item.name || '',
      is_active: item.is_active !== false,
    });
    setShowRawModal(true);
  };

  const handleSaveRawItem = async () => {
    if (!rawItemForm.name.trim()) {
      toast.error('Please enter raw material name');
      return;
    }

    setSaving(true);
    try {
      if (selectedRawItem?.id) {
        await adminApi.updateRawItem(selectedRawItem.id, rawItemForm);
        toast.success('Raw material updated');
      } else {
        await adminApi.createRawItem(rawItemForm);
        toast.success('Raw material created');
      }
      setShowRawModal(false);
      resetForm();
      await fetchRawItems();
      setRawItemsPage(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save raw material');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="raw-materials-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Raw Materials</h1>
        <p className="text-[#A8A8A8] mt-1">Create and manage raw material product names</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-[#2D2420]">Raw Items</h2>
            <Button onClick={handleOpenCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add Raw Material
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : rawItems.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
              <WarehouseIcon className="w-6 h-6 mr-2" />
              No raw items yet
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {pagedRawItems.map((item) => (
                      <tr key={item.id} className="hover:bg-[#FAFAF9]/60">
                        <td className="py-3 px-4 text-[#2D2420] font-medium">{item.name}</td>
                        <td className="py-3 px-4">
                          <Badge className={cn('text-xs', item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')}>
                            {item.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(item)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2">
                <p className="text-sm text-[#A8A8A8]">
                  Showing {(rawItemsPage - 1) * pageSize + 1} to {Math.min(rawItemsPage * pageSize, rawItems.length)} of {rawItems.length}
                </p>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={(e) => {
                          e.preventDefault();
                          setRawItemsPage((prev) => Math.max(1, prev - 1));
                        }}
                        className={rawItemsPage === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <span className="px-3 text-sm text-[#5C4033]">Page {rawItemsPage} of {rawItemsTotalPages}</span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        onClick={(e) => {
                          e.preventDefault();
                          setRawItemsPage((prev) => Math.min(rawItemsTotalPages, prev + 1));
                        }}
                        className={rawItemsPage >= rawItemsTotalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={showRawModal} onOpenChange={setShowRawModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">{selectedRawItem ? 'Update Raw Material' : 'Add Raw Material'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={rawItemForm.name}
                onChange={(e) => setRawItemForm((prev) => ({ ...prev, name: e.target.value }))}
                className="border-[#E5E5E5]"
              />
            </div>
            <div className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-3 py-2">
              <Label className="text-sm">Active</Label>
              <Switch
                checked={rawItemForm.is_active}
                onCheckedChange={(value) => setRawItemForm((prev) => ({ ...prev, is_active: value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRawModal(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSaveRawItem} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedRawItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
